import { FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import { HelpCircle, X } from "lucide-react";
import { useGetBooking } from "~/services/booking";
import { BookingTab, useDetailBookingStore } from "./detail-booking.store";
import { HeaderBooking } from "./header-booking";
import { QuickInfo } from "./quick-info";
import {
  Billing,
  BookingTabs,
  Communication,
  Documents,
  Inventory,
  Notes,
  Overview,
  Services,
  Tasks,
} from "./tabs";
import { Spin } from "antd";
import PageMeta from "~/components/common/meta";
import { ViewInventoryModal } from "./view-inventory-modal";
import { ExpoManagementModal } from "./expo-management-modal";

const tabs = {
  [BookingTab.OVERVIEW]: <Overview />,
  [BookingTab.BILLING]: <Billing />,
  [BookingTab.COMMUNICATION]: <Communication />,
  [BookingTab.DOCUMENTS]: <Documents />,
  [BookingTab.INVENTORY]: <Inventory />,
  [BookingTab.TASKS]: <Tasks />,
  [BookingTab.NOTES]: <Notes />,
  [BookingTab.SERVICES]: <Services />,
};

type Props = {};

export const DetailBooking: FC<Props> = () => {
  const { id } = useParams();

  const { data, isLoading, isRefetching } = useGetBooking(id);
  const { activeTab, setBooking, tutorialMode, toggleTutorialMode } = useDetailBookingStore();

  useEffect(() => {
    if (!data) return;
    setBooking(data);
  }, [data, setBooking]);

  // Don't render if there's no valid booking ID
  if (!id) {
    return (
      <div className="bg-gray-50 p-8">
        <div className="text-center text-red-600">
          <h2 className="text-xl font-semibold mb-2">Invalid Booking ID</h2>
          <p>No booking ID was provided in the URL.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <Spin spinning={isLoading || isRefetching} size="large">
        <PageMeta
          title={`Booking ${data?.referenceNumber ?? ""} Detail | Art Handler`}
        />
        <div className="mx-auto bg-white border border-gray-200 rounded-lg shadow-sm m-4">
          {/* Header with Back Button and Actions */}
          <HeaderBooking />

          {/* Quick Info Panel */}
          <QuickInfo />

          {/* Tab Navigation */}
          <BookingTabs />

          {/* Tab Content */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
            {/* Overview Tab Content */}
            {tabs[activeTab]}
          </div>
        </div>

        {/* View Inventory Modal */}
        <ViewInventoryModal />

        {/* Expo Management Modal */}
        <ExpoManagementModal />
      </Spin>

      {/* Tutorial Mode Toggle */}
      {tutorialMode ? (
        <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white px-6 py-2 flex items-center justify-between z-50 shadow-lg">
          <div className="flex items-center gap-2 text-sm">
            <HelpCircle size={16} />
            <span className="font-medium">Tutorial Mode Active</span>
            <span className="text-blue-200">— Hover over elements to see descriptions</span>
          </div>
          <button
            onClick={toggleTutorialMode}
            className="flex items-center gap-1 px-3 py-1 rounded bg-blue-500 hover:bg-blue-400 text-sm"
          >
            <X size={14} />
            Exit
          </button>
        </div>
      ) : (
        <div className="flex justify-center py-4">
          <button
            onClick={toggleTutorialMode}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-transparent hover:border-blue-200 transition-colors"
          >
            <HelpCircle size={16} />
            Tutorial Mode
          </button>
        </div>
      )}
    </div>
  );
};
